// Location: src/features/devices/components/DeviceCreate.tsx

import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceInput,
  required,
  useTranslate,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Devices as DeviceIcon } from '@mui/icons-material';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { MyFormLayout, FormCard, MyFormSection } from '@/components/forms/MyFormLayout';
import { MyToolbar } from '@/components/forms/MyToolbar';
import {
  validateInstallationCode,
  validateHotelId,
} from '../validators/deviceValidators';
import { 
  getCategoryChoices,
  transformDeviceForCreate,
} from '../config/deviceCategories';

/**
 * Validation for category field
 */
const validateCategory = (value: any) => {
  if (!value) {
    return 'resources.devices.validation.categoryRequired';
  }
  return undefined;
};

/**
 * Validation rules for device creation
 */
const validateInstallationCodeRules = [
  required('resources.devices.validation.installationCodeRequired'),
  validateInstallationCode,
];

const validateHotelIdRules = [
  required('resources.devices.validation.hotelRequired'),
  validateHotelId,
];

const validateCategoryRules = [
  required('resources.devices.validation.categoryRequired'),
  validateCategory,
];

/**
 * Device creation component
 * Only SuperAdmin can access this
 */
export const DeviceCreate = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();

  const handleCancel = () => navigate('/admin-options?tab=devices');

  const onSuccess = () => {
    notify('resources.devices.messages.createSuccess', { type: 'success' });
    redirect('/admin-options?tab=devices');
  };

  const onError = (error: any) => {
    const errorMessage = error?.body?.error?.message || 
                        error?.message || 
                        translate('resources.devices.messages.createError');
    notify(errorMessage, { type: 'error' });
  };

  /**
   * Transform form data before sending to API
   * Converts category to deviceType + channel
   */
  const transform = (data: any) => {
    return transformDeviceForCreate(data);
  };

  return (
    <Create
      mutationOptions={{ onSuccess, onError }}
      redirect="list"
      title={translate('resources.devices.create.title')}
      transform={transform}
    >
      <SimpleForm toolbar={<MyToolbar resource="devices" onCancel={handleCancel} />}>
        <MyFormContainer maxWidth="md">
          <MyFormLayout centered maxColumns={1}>
            <FormCard xs={12}>
              <MyFormSection
                title={translate('resources.devices.create.title')}
                subtitle={translate('resources.devices.create.subtitle')}
                icon={<DeviceIcon />}
                variant="outlined"
              >
                {/* Installation Code */}
                <TextInput
                  source="installationCode"
                  label={translate('resources.devices.fields.installationCode')}
                  validate={validateInstallationCodeRules}
                  fullWidth
                  helperText={translate('resources.devices.hints.installationCode')}
                />

                {/* Name (optional) */}
                <TextInput
                  source="name"
                  label={translate('resources.devices.fields.name')}
                  fullWidth
                  helperText={translate('resources.devices.hints.name')}
                />

                {/* Device Category (replaces deviceType + channel) */}
                <SelectInput
                  source="category"
                  label={translate('resources.devices.fields.category')}
                  validate={validateCategoryRules}
                  choices={getCategoryChoices(translate)}
                  fullWidth
                  helperText={translate('resources.devices.hints.category')}
                />

                {/* Hotel (required) */}
                <ReferenceInput
                  source="hotelId"
                  reference="hotels"
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <SelectInput
                    label={translate('resources.devices.fields.hotel')}
                    optionText="name"
                    validate={validateHotelIdRules}
                    fullWidth
                  />
                </ReferenceInput>
              </MyFormSection>
            </FormCard>
          </MyFormLayout>
        </MyFormContainer>
      </SimpleForm>
    </Create>
  );
};

export default DeviceCreate;